import { HTMLAttributes, PropsWithChildren } from "react";
import {
  gapClassName,
  widthClassName,
  flexJustifyClassName,
  colSpanClassName,
  growClassName,
} from "./commonClassNames";
import { ColSpanProp, GapProp, GrowProp, WidthProp, FlexJustifyProp } from "./commonProps";

const viewportSizes = ["small", "medium"] as const;
type ViewportSize = typeof viewportSizes[number];

type BorderWidth = "thin" | "medium";

export type FlexProps = HTMLAttributes<HTMLDivElement> &
  ColSpanProp &
  GapProp &
  GrowProp &
  FlexJustifyProp &
  WidthProp &
  Partial<{
    /**
     * Highlight the flex container.
     */
    __debug: boolean;
    border: BorderWidth;
    borderTop: BorderWidth;
    borderBottom: BorderWidth;
    borderLeft: BorderWidth;
    borderRight: BorderWidth;
    direction: "row" | "col" | "rowCol";
    /**
     * Element height, reference: https://tailwindcss.com/docs/height
     */
    height: 4 | 6 | 12 | 24 | 44;
    inline: boolean;
    items: "start" | "end" | "center" | "baseline" | "stretch";
    /**
     * Overflow, reference: https://tailwindcss.com/docs/overflow
     */
    overflow: "auto" | "hidden" | "truncate" | "clip" | "scroll" | "visible";
    horizontalMargin: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
    horizontalPadding: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 16;
    verticalMargin: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    verticalPadding: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 16;
    viewport: ViewportSize;
    wrap: boolean;
  }>;

function flexDirectionClassName({ direction }: Pick<FlexProps, "direction">) {
  switch (direction) {
    case "col":
      return "flex-col";
    case "row":
      return "flex-row";
    case "rowCol":
      return "flex-col md:flex-row";
    default:
      return "";
  }
}

function flexItemsClassName({ items }: Pick<FlexProps, "items">) {
  switch (items) {
    case "start":
      return "items-start";
    case "end":
      return "items-end";
    case "center":
      return "items-center";
    case "baseline":
      return "items-evenly";
    case "stretch":
      return "items-stretch";
    default:
      return "";
  }
}

function fixedHeightClassName({ height }: Pick<FlexProps, "height">) {
  switch (height) {
    case 4:
      return "h-4";
    case 6:
      return "h-6";
    case 12:
      return "h-12";
    case 24:
      return "h-24";
    case 44:
      return "h-44";
    default:
      return "";
  }
}

function horizontalMarginClassName({ horizontalMargin }: Pick<FlexProps, "horizontalMargin">) {
  switch (horizontalMargin) {
    case 8:
      return "mx-4 md:mx-8";
    case 7:
      return "mx-3 md:mx-7";
    case 6:
      return "mx-3 md:mx-6";
    case 5:
      return "mx-3 md:mx-5";
    case 4:
      return "mx-2 md:mx-4";
    case 3:
      return "mx-2 md:mx-3";
    case 2:
      return "mx-1 md:mx-2";
    case 1:
      return "mx-1";
    case 0:
    default:
      return "";
  }
}

function horizontalPaddingClassName({ horizontalPadding }: Pick<FlexProps, "horizontalPadding">) {
  switch (horizontalPadding) {
    case 16:
      return "px-6 md:px-16";
    case 12:
      return "px-6 md:px-12";
    case 11:
      return "px-5 md:px-11";
    case 10:
      return "px-5 md:px-10";
    case 9:
      return "px-4 md:px-9";
    case 8:
      return "px-4 md:px-8";
    case 7:
      return "px-3 md:px-7";
    case 6:
      return "px-3 md:px-6";
    case 5:
      return "px-3 md:px-5";
    case 4:
      return "px-2 md:px-4";
    case 3:
      return "px-2 md:px-3";
    case 2:
      return "px-1 md:px-2";
    case 1:
      return "px-1";
    case 0:
    default:
      return "";
  }
}

function verticalMarginClassName({ verticalMargin }: Pick<FlexProps, "verticalMargin">) {
  switch (verticalMargin) {
    case 12:
      return "my-6 md:my-12";
    case 11:
      return "my-5 md:my-11";
    case 10:
      return "my-5 md:my-10";
    case 9:
      return "my-4 md:my-9";
    case 8:
      return "my-4 md:my-8";
    case 7:
      return "my-3 md:my-7";
    case 6:
      return "my-3 md:my-6";
    case 5:
      return "my-3 md:my-5";
    case 4:
      return "my-2 md:my-4";
    case 3:
      return "my-2 md:my-3";
    case 2:
      return "my-1 md:my-2";
    case 1:
      return "my-1";
    case 0:
    default:
      return "";
  }
}

function verticalPaddingClassName({ verticalPadding }: Pick<FlexProps, "verticalPadding">) {
  switch (verticalPadding) {
    case 16:
      return "py-6 md:py-16";
    case 12:
      return "py-6 md:py-12";
    case 11:
      return "py-5 md:py-11";
    case 10:
      return "py-5 md:py-10";
    case 9:
      return "py-4 md:py-9";
    case 8:
      return "py-4 md:py-8";
    case 7:
      return "py-3 md:py-7";
    case 6:
      return "py-3 md:py-6";
    case 5:
      return "py-3 md:py-5";
    case 4:
      return "py-2 md:py-4";
    case 3:
      return "py-2 md:py-3";
    case 2:
      return "py-1 md:py-2";
    case 1:
      return "py-1";
    case 0:
    default:
      return "";
  }
}

function viewportSizeClassName({ inline, viewport }: Pick<FlexProps, "inline" | "viewport">) {
  const display = inline ? "inline-flex" : "flex";

  switch (viewport) {
    case "small":
      return `${display} md:hidden`;
    case "medium":
      return `hidden md:${display}`;
    default:
      return display;
  }
}

function borderClassName({ border }: Pick<FlexProps, "border">) {
  switch (border) {
    case "thin":
      return "border border-neutral-110";
    case "medium":
      return "border-2 border-neutral-105";
    default:
      return "";
  }
}

function borderTopClassName({ borderTop }: Pick<FlexProps, "borderTop">) {
  switch (borderTop) {
    case "thin":
      return "border-t border-neutral-110";
    case "medium":
      return "border-bt2 border-neutral-105";
    default:
      return "";
  }
}

function borderBottomClassName({ borderBottom }: Pick<FlexProps, "borderBottom">) {
  switch (borderBottom) {
    case "thin":
      return "border-b border-neutral-110";
    case "medium":
      return "border-b-2 border-neutral-105";
    default:
      return "";
  }
}

function borderLeftClassName({ borderLeft }: Pick<FlexProps, "borderLeft">) {
  switch (borderLeft) {
    case "thin":
      return "border-l border-neutral-110";
    case "medium":
      return "border-l-2 border-neutral-105";
    default:
      return "";
  }
}

function borderRightClassName({ borderRight }: Pick<FlexProps, "borderRight">) {
  switch (borderRight) {
    case "thin":
      return "border-r border-neutral-110";
    case "medium":
      return "border-r-2 border-neutral-105";
    default:
      return "";
  }
}

function overflowClassName({ overflow }: Pick<FlexProps, "overflow">) {
  switch (overflow) {
    case "auto":
      return "overflow-auto";
    case "hidden":
      return "overflow-hidden";
    case "clip":
      return "overflow-clip";
    case "visible":
      return "overflow-visible";
    case "scroll":
      return "overflow-scroll";
    case "truncate":
      return "overflow-hidden truncate";

    default:
      return "";
  }
}

/** @deprecated use div with tailwind styles directly */
export const Flex: React.FC<PropsWithChildren<FlexProps>> = ({
  children,
  __debug,
  border,
  borderTop,
  borderBottom,
  borderLeft,
  borderRight,
  colSpan,
  direction,
  gap,
  grow,
  height,
  horizontalMargin,
  horizontalPadding,
  inline,
  items,
  justify,
  onClick,
  overflow,
  verticalMargin,
  verticalPadding,
  viewport,
  width,
  wrap,
  className,
  ...props
}) => {
  const combinedClassName = [
    __debug ? "border border-magenta" : "",
    borderClassName({ border }),
    borderTopClassName({ borderTop }),
    borderBottomClassName({ borderBottom }),
    borderLeftClassName({ borderLeft }),
    borderRightClassName({ borderRight }),
    colSpanClassName({ colSpan }),
    fixedHeightClassName({ height }),
    widthClassName({ width }),
    flexDirectionClassName({ direction }),
    flexItemsClassName({ items }),
    flexJustifyClassName({ justify }),
    gapClassName({ gap }),
    growClassName({ grow }),
    horizontalMarginClassName({ horizontalMargin }),
    horizontalPaddingClassName({ horizontalPadding }),
    onClick ? "cursor-pointer" : "",
    overflowClassName({ overflow }),
    verticalMarginClassName({ verticalMargin }),
    verticalPaddingClassName({ verticalPadding }),
    viewportSizeClassName({ inline, viewport }),
    wrap ? "flex-wrap" : "",
    className ?? "",
  ].join(" ");

  return (
    <div {...props} className={combinedClassName} onClick={onClick}>
      {children}
    </div>
  );
};
